import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useInventory } from '../context/InventoryContext';
import { useAnalytics } from '../context/AnalyticsContext';
import VehicleRequestForm from '../components/VehicleRequestForm';

const { FiArrowLeft, FiHeart, FiShare2, FiPhone, FiMessageCircle, FiCalendar, FiDollarSign, FiCar, FiGauge, FiSettings, FiMail } = FiIcons;

const VehicleDetail = () => {
  const { id } = useParams();
  const { getVehicleById } = useInventory();
  const { trackEvent } = useAnalytics();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);

  const vehicle = getVehicleById(id);

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-700 mb-4">Vehicle Not Found</h1>
          <Link
            to="/inventory"
            className="bg-dealership-blue text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Inventory
          </Link>
        </div>
      </div>
    );
  }

  const handleTestDriveRequest = () => {
    trackEvent('test_drive_request', { vehicleId: vehicle.id });
    setShowContactForm(true);
  };

  const handleContactRequest = () => {
    trackEvent('form_fill', { type: 'contact', vehicleId: vehicle.id });
    // Would integrate with contact system
    alert('Thank you for your interest! We\'ll contact you shortly.');
    setShowContactForm(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to="/inventory"
            className="inline-flex items-center space-x-2 text-dealership-blue hover:text-blue-700 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back to Inventory</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="relative"
            >
              <img
                src={vehicle.images[currentImageIndex]}
                alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
              
              {vehicle.images.length > 1 && (
                <div className="flex space-x-2 mt-4">
                  {vehicle.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                        index === currentImageIndex ? 'border-dealership-blue' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`View ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Vehicle Details */}
          <div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-dealership-dark mb-2">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h1>
                  <p className="text-gray-600">{vehicle.type} • {vehicle.mileage.toLocaleString()} miles</p>
                </div>
                <div className="flex space-x-2">
                  <button className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                    <SafeIcon icon={FiHeart} className="w-6 h-6" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-dealership-blue transition-colors">
                    <SafeIcon icon={FiShare2} className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="text-4xl font-bold text-dealership-blue mb-6">
                ${vehicle.price.toLocaleString()}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCar} className="w-5 h-5 text-dealership-blue" />
                  <div>
                    <p className="text-sm text-gray-600">Transmission</p>
                    <p className="font-semibold">{vehicle.transmission}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiGauge} className="w-5 h-5 text-dealership-blue" />
                  <div>
                    <p className="text-sm text-gray-600">Fuel Economy</p>
                    <p className="font-semibold">{vehicle.mpg}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiSettings} className="w-5 h-5 text-dealership-blue" />
                  <div>
                    <p className="text-sm text-gray-600">Engine</p>
                    <p className="font-semibold">{vehicle.engine}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-dealership-blue" />
                  <div>
                    <p className="text-sm text-gray-600">Fuel Type</p>
                    <p className="font-semibold">{vehicle.fuelType}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={handleTestDriveRequest}
                    className="bg-dealership-blue text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                    <span>Schedule Test Drive</span>
                  </button>
                  
                  <button
                    onClick={() => setShowRequestForm(true)}
                    className="bg-dealership-gold text-white py-3 px-6 rounded-lg font-semibold hover:bg-yellow-600 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiMail} className="w-5 h-5" />
                    <span>Request Info</span>
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowContactForm(true)}
                    className="bg-white border-2 border-dealership-blue text-dealership-blue py-2 px-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiPhone} className="w-4 h-4" />
                    <span>Contact</span>
                  </button>
                  <button className="bg-white border-2 border-dealership-blue text-dealership-blue py-2 px-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center space-x-2">
                    <SafeIcon icon={FiMessageCircle} className="w-4 h-4" />
                    <span>Chat</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Vehicle Details Sections */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Description */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-dealership-dark mb-4">Description</h3>
            <p className="text-gray-600 leading-relaxed">{vehicle.description}</p>
          </div>

          {/* Features */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-dealership-dark mb-4">Features</h3>
            <div className="grid grid-cols-1 gap-2">
              {vehicle.features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-dealership-blue rounded-full"></div>
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Specifications */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h3 className="text-xl font-bold text-dealership-dark mb-4">Specifications</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Exterior Color</span>
                <span className="font-semibold">{vehicle.exterior}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Interior</span>
                <span className="font-semibold">{vehicle.interior}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Year</span>
                <span className="font-semibold">{vehicle.year}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Mileage</span>
                <span className="font-semibold">{vehicle.mileage.toLocaleString()} miles</span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form Modal */}
        {showContactForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-lg p-6 w-full max-w-md"
            >
              <h3 className="text-xl font-bold text-dealership-dark mb-4">
                Contact Us About This Vehicle
              </h3>
              <form onSubmit={(e) => { e.preventDefault(); handleContactRequest(); }}>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dealership-blue"
                  />
                  <input
                    type="email"
                    placeholder="Your Email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dealership-blue"
                  />
                  <input
                    type="tel"
                    placeholder="Your Phone Number"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dealership-blue"
                  />
                  <textarea
                    placeholder="Message (optional)"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dealership-blue"
                  ></textarea>
                </div>
                <div className="flex space-x-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-dealership-blue text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Send Message
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowContactForm(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
        
        {/* Vehicle Request Form Modal */}
        <VehicleRequestForm 
          vehicle={vehicle} 
          isOpen={showRequestForm} 
          onClose={() => setShowRequestForm(false)} 
        />
      </div>
    </div>
  );
};

export default VehicleDetail;